import { Button, Chip, Spinner, Switch } from "@heroui/react";
import { Checkbox } from "@heroui/react";
import { ExternalLink, RefreshCw, ShoppingBag, Tag } from "lucide-react";
import { useCallback, useState } from "react";

import {
  ComboField,
  DataTable,
  ViewSheet,
  notify,
  type DataGridColumn,
} from "@/components/kit";
import { useGetAllCategories } from "@/hooks/api/category/useGetAllCategories";
import { useRefreshScrapedCategory } from "@/hooks/api/scrape-category/useRefreshScrapedCategory";
import { useUpdateScrapedCategory } from "@/hooks/api/scrape-category/useUpdateScrapeCategory";
import { useUpdateScrapedCategoryBrand } from "@/hooks/api/scrape-category/useUpdateScrapeCategoryBrand";
import { useGetAllSuppliers } from "@/hooks/api/supplier";
import { ScrapedCategory, ScrapedCategoryBrand } from "@/types/scrape-category";

interface ScrapedCategoryViewModalProps {
  isOpen: boolean;
  category: ScrapedCategory | null;
  onClose: () => void;
  setCategory: (category: ScrapedCategory) => void;
}

const Stat = ({
  value,
  label,
  of,
  dot,
}: {
  value: number;
  label: string;
  of?: number;
  dot?: string;
}) => (
  <div className="flex flex-col gap-0.5">
    <span className="text-xl font-semibold tabular-nums">
      {value}
      {of !== undefined ? (
        <span className="text-muted text-sm font-normal"> / {of}</span>
      ) : null}
    </span>
    <span className="text-muted flex items-center gap-1.5 text-xs">
      {dot ? (
        <span
          aria-hidden="true"
          className="size-1.5 rounded-full"
          style={{ backgroundColor: `var(${dot})` }}
        />
      ) : null}
      {label}
    </span>
  </div>
);

const Section = ({
  title,
  action,
  children,
}: {
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) => (
  <section className="flex flex-col gap-3">
    <div className="flex items-center justify-between gap-3">
      <h3 className="text-sm font-semibold">{title}</h3>
      {action}
    </div>
    {children}
  </section>
);

export const ScrapedCategoryViewModal = ({
  category,
  isOpen,
  onClose,
  setCategory,
}: ScrapedCategoryViewModalProps) => {
  const { data: suppliers } = useGetAllSuppliers();
  const { data: categories } = useGetAllCategories();
  const { mutate: updateCategory, isPending: isUpdating } =
    useUpdateScrapedCategory();
  const { mutate: updateBrand } = useUpdateScrapedCategoryBrand();
  const [updatingBrandId, setUpdatingBrandId] = useState<string | null>(null);
  const { mutate: refreshCategory, isPending: isRefreshing } =
    useRefreshScrapedCategory();

  const handleRefresh = useCallback(() => {
    if (!category) return;

    refreshCategory(category.id, {
      onSuccess: (freshCategory) => {
        setCategory(freshCategory);
        notify.success(
          "Category refreshed",
          "The category data has been updated successfully.",
        );
      },
      onError: (error: any) => {
        notify.error(
          "Failed to refresh category",
          error.response?.data?.message ||
            "Failed to refresh category data. Please try again.",
        );
      },
    });
  }, [category, refreshCategory, setCategory]);

  const handleUpdateCategoryblacklist = useCallback(
    (newValue: boolean) => {
      if (!category) return;

      setCategory({ ...category, blacklist: newValue });

      updateCategory(
        {
          id: category.id,
          blacklist: newValue,
        },
        {
          onError: (error: any) => {
            notify.error(
              `Failed to update category ${category.name}`,
              error.response?.data?.message ||
                "Failed to update blacklist status. Please try again.",
            );
          },
        },
      );
    },
    [category, updateCategory, setCategory],
  );

  const handleUpdateCategoryLinkedCategory = useCallback(
    (linkedCategoryId: string | null) => {
      if (!category) return;

      setCategory({ ...category, linkedCategoryId });

      updateCategory(
        {
          id: category.id,
          linkedCategoryId,
        },
        {
          onError: (error: any) => {
            notify.error(
              `Failed to update category ${category.name}`,
              error.response?.data?.message ||
                "Failed to update linked category. Please try again.",
            );
          },
        },
      );
    },
    [category, updateCategory, setCategory],
  );

  const handleUpdateCategoryBrandBlacklist = useCallback(
    (categoryBrand: ScrapedCategoryBrand, blacklist: boolean) => {
      if (!category) return;

      setUpdatingBrandId(categoryBrand.brand.id);

      setCategory({
        ...category,
        brandRelations: category.brandRelations.map((b) =>
          b.id === categoryBrand.id ? { ...b, blacklist } : b,
        ),
      });

      updateBrand(
        {
          categoryId: category.id,
          brandId: categoryBrand.brand.id,
          blacklist,
        },
        {
          onError: (error: any) => {
            notify.error(
              `Failed to update brand ${categoryBrand.brand.name}`,
              error.response?.data?.message ||
                "Failed to update sync option. Please try again.",
            );
          },
          onSettled: () => {
            setUpdatingBrandId(null);
          },
        },
      );
    },
    [category, updateBrand, setCategory],
  );

  const brandLinks = (categoryBrand: ScrapedCategoryBrand) =>
    category ? (
      <div className="flex items-center justify-end gap-1">
        <a
          aria-label={`View brand ${categoryBrand.brand.name}`}
          className="button button--ghost button--icon-only button--sm"
          href={`/scraped/brands?name=${categoryBrand.brand.name}`}
          rel="noreferrer"
          target="_blank"
          title="View brand"
        >
          <Tag className="size-4" />
        </a>
        <a
          aria-label={`Browse products of ${categoryBrand.brand.name}`}
          className="button button--ghost button--icon-only button--sm"
          href={`/scraped/products?categoryIds=${category.id}:${encodeURIComponent(category.name)}&brandIds=${categoryBrand.brand.id}:${encodeURIComponent(categoryBrand.brand.name)}`}
          rel="noreferrer"
          target="_blank"
          title="Browse products"
        >
          <ShoppingBag className="size-4" />
        </a>
      </div>
    ) : null;

  const supplierName = (categoryBrand: ScrapedCategoryBrand) =>
    suppliers?.find((s) => s.id === categoryBrand.supplierId)?.name ||
    "Unknown Supplier";

  const whitelistedColumns: DataGridColumn<ScrapedCategoryBrand>[] = [
    {
      id: "name",
      header: "Name",
      isRowHeader: true,
      cell: (b) => <span className="font-medium">{b.brand.name}</span>,
    },
    {
      id: "supplierName",
      header: "Supplier name",
      cell: (b) => <span className="text-muted">{supplierName(b)}</span>,
    },
    {
      id: "productsCount",
      header: "Products",
      align: "end",
      cell: (b) => <span className="tabular-nums">{b.productsCount || 0}</span>,
    },
    {
      id: "blacklist",
      header: "Blacklist",
      width: 90,
      cell: (b) =>
        updatingBrandId === b.brand.id ? (
          <Spinner size="sm" />
        ) : (
          <Checkbox
            aria-label={`Blacklist ${b.brand.name} in this category`}
            isSelected={b.blacklist}
            variant="secondary"
            onChange={() => handleUpdateCategoryBrandBlacklist(b, !b.blacklist)}
          >
            <Checkbox.Content>
              <Checkbox.Control>
                <Checkbox.Indicator />
              </Checkbox.Control>
            </Checkbox.Content>
          </Checkbox>
        ),
    },
    {
      id: "actions",
      header: <span className="sr-only">Actions</span>,
      width: 90,
      align: "end",
      cell: brandLinks,
    },
  ];

  const blacklistedColumns = whitelistedColumns.filter(
    (column) => column.id !== "blacklist",
  );

  // Create new object reference for updating brand to force table row re-render
  const whitelistedBrands =
    category?.brandRelations
      .filter((r) => r.brand.blacklist == false)
      .map((r) => (r.brand.id === updatingBrandId ? { ...r } : r)) ?? [];
  const blacklistedBrands =
    category?.brandRelations.filter((r) => r.brand.blacklist) ?? [];

  if (!category) return null;

  const total = category.brandRelations.length;
  const globallyBlocked = category.brandRelations.filter(
    (rel) => rel.brand.blacklist,
  ).length;
  const categoryBlocked = category.brandRelations.filter(
    (rel) => rel.blacklist && !rel.brand.blacklist,
  ).length;
  const whitelisted = category.brandRelations.filter(
    (rel) => !rel.blacklist && !rel.brand.blacklist,
  ).length;
  const products = category.productsCount || 0;
  const supplier = suppliers?.find((s) => s.id === category.supplierId);

  return (
    <ViewSheet
      action={
        <Button
          isPending={isRefreshing}
          size="sm"
          variant="outline"
          onPress={handleRefresh}
        >
          <RefreshCw className="size-4" />
          Refresh
        </Button>
      }
      description={`${category.name} · ${supplier?.name ?? "Unknown Supplier"}`}
      isOpen={isOpen}
      title="Category Details"
      onClose={onClose}
    >
      <Section
        action={
          isUpdating ? (
            <Chip color="accent" size="sm" variant="soft">
              <Spinner size="sm" />
              Updating...
            </Chip>
          ) : null
        }
        title="Category information"
      >
        <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1">
            <span className="text-sm font-medium">Name</span>
            <span>{category.name}</span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-sm font-medium">Supplier ID</span>
            <span className="font-mono text-xs">{category.supplierId}</span>
          </div>
          <ComboField
            items={categories ?? []}
            label="Linked category"
            placeholder="Not linked"
            value={category.linkedCategoryId ?? ""}
            onChange={(id) => handleUpdateCategoryLinkedCategory(id || null)}
          />
          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium" id="cat-blacklist-label">
              Blacklist
            </span>
            <Switch
              aria-labelledby="cat-blacklist-label"
              isSelected={category.blacklist}
              size="sm"
              onChange={handleUpdateCategoryblacklist}
            >
              <Switch.Content>
                <Switch.Control>
                  <Switch.Thumb />
                </Switch.Control>
                <span className="text-muted text-sm">
                  {category.blacklist ? "Blacklisted" : "Not blacklisted"}
                </span>
              </Switch.Content>
            </Switch>
          </div>
        </div>
      </Section>

      <Section title="Brand breakdown">
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <Stat label="Total brands" value={total} />
          <Stat
            dot="--danger"
            label="Globally blocked"
            value={globallyBlocked}
          />
          <Stat
            dot="--warning"
            label="Category blocked"
            value={categoryBlocked}
          />
          <Stat dot="--success" label="Whitelisted" value={whitelisted} />
        </div>
        <p className="text-muted text-xs">
          Globally blocked brands are blocked across all categories. Category
          blocked brands are blocked only for this category. Whitelisted brands
          are active and available for use.
        </p>
      </Section>

      <Section
        action={
          <a
            className="button button--ghost button--sm"
            href={`/scraped/products?categoryIds=${category.id}:${encodeURIComponent(category.name)}`}
            rel="noreferrer"
            target="_blank"
          >
            Browse all products
            <ExternalLink className="size-3.5" />
          </a>
        }
        title="Product statistics"
      >
        <div className="grid grid-cols-2 gap-x-4 gap-y-5 sm:grid-cols-3">
          <Stat label="Total products" value={products} />
          <Stat
            dot="--success"
            label="Whitelisted"
            of={products}
            value={category.whitelistedProductsCount || 0}
          />
          <Stat
            dot="--danger"
            label="Blacklisted"
            of={products}
            value={category.blacklistedProductsCount || 0}
          />
          <Stat
            dot="--accent"
            label="With brand"
            of={products}
            value={category.productsWithBrandCount || 0}
          />
          <Stat
            dot="--warning"
            label="Without brand"
            of={products}
            value={category.productsWithoutBrandCount || 0}
          />
          <Stat
            dot="--muted"
            label="Out of stock"
            of={products}
            value={category.outOfStockProductsCount || 0}
          />
        </div>
      </Section>

      <Section title={`Whitelisted brands (${whitelistedBrands.length})`}>
        <p className="text-muted -mt-1 text-xs">
          The brands that are whitelisted and also belong to this category.
          Blacklisting one here blacklists only the products in this category
          and brand; the brand&apos;s other products are untouched.
        </p>
        <div className="relative">
          <DataTable
            compact
            columns={whitelistedColumns}
            empty={{ title: "No brands found" }}
            getRowId={(b) => b.id}
            label="Whitelisted brands"
            rows={whitelistedBrands}
          />
          {category.blacklist ? (
            <div className="bg-surface/80 absolute inset-0 z-10 flex items-center justify-center rounded-2xl p-4 text-center backdrop-blur-sm">
              <div className="flex max-w-xs flex-col gap-1">
                <p className="font-medium">Category blacklisted</p>
                <p className="text-muted text-sm">
                  This category is blacklisted. Whitelist the category above to
                  manage its brand relations.
                </p>
              </div>
            </div>
          ) : null}
        </div>
      </Section>

      <Section title={`Blacklisted brands (${blacklistedBrands.length})`}>
        <p className="text-muted -mt-1 text-xs">
          These brands are blacklisted everywhere. To whitelist one, open it on
          the brands page, turn off its blacklist there, then press Refresh
          here.
        </p>
        <DataTable
          compact
          columns={blacklistedColumns}
          empty={{ title: "No brands found" }}
          getRowId={(b) => b.id}
          label="Blacklisted brands"
          rows={blacklistedBrands}
        />
      </Section>
    </ViewSheet>
  );
};
